import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  FaArrowLeft,
  FaSyncAlt,
  FaFileExcel,
  FaSearch,
  FaFilter,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTachometerAlt,
  FaThermometerHalf,
  FaMapMarkerAlt,
  FaTruck,
  FaHistory,
  FaInfoCircle,
} from "react-icons/fa";
import "./TpmsMonitoring.css";

const TpmsMonitoring = () => {
  const navigate = useNavigate();
  const [activeTab] = useState("latest");
 
  // Latest TPMS State
  const [latestData, setLatestData] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    normal: 0,
    lowPressure: 0,
    highPressure: 0,
    leakage: 0,
    totalAlerts: 0,
  });
  const [isMock, setIsMock] = useState(false);
  const [mockMessage, setMockMessage] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fetchProgress, setFetchProgress] = useState(null);
  const latestRequest = useRef(null);
  const eventsRequest = useRef(null);
  const readingQuery = useRef({ start: "", end: "" });
  const [readingStart, setReadingStart] = useState("");
  const [readingEnd, setReadingEnd] = useState("");
  const [appliedReading, setAppliedReading] = useState({ start: "", end: "" });
  const historyQuery = useRef(null);
  const [appliedHistory, setAppliedHistory] = useState(null);
  const [historyProgress, setHistoryProgress] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Filters for Latest
  const [search, setSearch] = useState("");
  const [alertFilter, setAlertFilter] = useState("ALL");
  const [siteFilter, setSiteFilter] = useState("ALL");

  // Historical Events State (Script 1)
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [eventVehicleFilter, setEventVehicleFilter] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const baseUrl = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000";
  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // Poll the shared fleet refresh without holding an HTTP request open.
  const fetchLatest = useCallback(async (forceRefresh = false, range = readingQuery.current) => {
    if ((range.start || range.end) && (!range.start || !range.end || range.start > range.end || (Date.parse(range.end) - Date.parse(range.start)) / 86400000 >= 31)) {
      setErrorMsg("Choose both dates in order, up to 31 days per report.");
      return;
    }
    if (JSON.stringify(range) !== JSON.stringify(readingQuery.current)) setLatestData([]);
    readingQuery.current = { ...range };
    setAppliedReading({ ...range });
    latestRequest.current?.abort();
    const controller = new AbortController();
    latestRequest.current = controller;
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setErrorMsg("");
    setFetchProgress(null);
    try {
      let firstRequest = true;
      let readingPage = 1;
      const collectedReadings = [];
      while (!controller.signal.aborted) {
        const refresh = firstRequest && forceRefresh ? "?refresh=true" : "";
        const rangeParams = new URLSearchParams({ start_date: range.start, end_date: range.end, view: "readings", page_size: "250", page: String(readingPage) });
        const res = await axios.get(range.start ? `${baseUrl}/api/tpms/events?${rangeParams}` : `${baseUrl}/api/tpms/latest${refresh}`, {
          headers: getHeaders(), signal: controller.signal, timeout: 15000,
        });
        if (controller.signal.aborted) return;
        firstRequest = false;
        if (res.data.loading) {
          setFetchProgress(res.data.progress || { checked: 0, total: 0 });
          if (res.data.rows?.length) {
            setLatestData(res.data.rows);
            setSummary(res.data.summary);
            setLastUpdated(res.data.lastUpdated || "");
          }
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        if (!res.data.success) throw new Error(res.data.error || "Failed to load TPMS readings.");
        setErrorMsg(res.data.error || "");
        collectedReadings.push(...(res.data.rows || []));
        if (range.start && collectedReadings.length < res.data.total) { readingPage++; continue; }
        setLatestData(collectedReadings);
        if (res.data.summary) setSummary(res.data.summary);
        setLastUpdated(res.data.lastUpdated || "");
        break;
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error("Error fetching TPMS latest:", err);
      setErrorMsg(err.response?.data?.error || err.response?.data?.message || err.message || "Failed to load TPMS readings.");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setRefreshing(false);
        setFetchProgress(null);
      }
    }
  }, [baseUrl]);

  const sanitizeHistoryPage = value => Math.max(1, Math.floor(Number(value) || 1));

  const fetchEvents = useCallback(async (nextPage = 1, filters = historyQuery.current) => {
    if (!filters) return;
    if (!filters.start || !filters.end || filters.start > filters.end) {
      setEventsError("Choose both dates, with the start date on or before the end date.");
      return;
    }
    if ((Date.parse(filters.end) - Date.parse(filters.start)) / 86400000 >= 31) {
      setEventsError("Choose a date range of 31 days or fewer.");
      return;
    }
    const safePage = sanitizeHistoryPage(nextPage);
    const changed = JSON.stringify(filters) !== JSON.stringify(historyQuery.current);
    historyQuery.current = { ...filters };
    setAppliedHistory({ ...filters });
    eventsRequest.current?.abort();
    const controller = new AbortController();
    eventsRequest.current = controller;
    if (changed) { setEvents([]); setEventsTotal(0); setPage(1); }
    setEventsLoading(true);
    setEventsError("");
    setHistoryProgress(null);
    try {
      const params = new URLSearchParams({ start_date: filters.start, end_date: filters.end,
        vehicle_reg: filters.vehicle || "", page: String(safePage), page_size: String(pageSize) });
      while (!controller.signal.aborted) {
        const { data } = await axios.get(`${baseUrl}/api/tpms/events?${params}`, {
          headers: getHeaders(), signal: controller.signal, timeout: 15000,
        });
        if (controller.signal.aborted) return;
        if (data.loading) {
          setHistoryProgress(data.progress || { checked: 0, total: 0 });
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        if (!data.success) throw new Error(data.error || "Failed to fetch events.");
        setEvents(data.rows || []);
        setEventsTotal(Number(data.total ?? data.rows?.length ?? 0));
        setPage(Number(data.page) || safePage);
        return;
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setEventsError(error.response?.data?.error || error.response?.data?.message || error.message || "Failed to fetch events.");
      }
    } finally {
      if (!controller.signal.aborted) { setEventsLoading(false); setHistoryProgress(null); }
    }
  }, [baseUrl]);

  const historyPageCount = Math.max(1, Math.ceil(eventsTotal / pageSize));
  const safePageNumber = Math.min(page, historyPageCount);
  const pagedEvents = useMemo(() => events, [events]);

  useEffect(() => {
    fetchLatest();
    return () => latestRequest.current?.abort();
  }, [fetchLatest]);

  useEffect(() => {
    if (activeTab === "events" && !historyQuery.current) {
      fetchEvents(1, { start: startDate, end: endDate, vehicle: eventVehicleFilter.trim() });
    }
  }, [activeTab, fetchEvents, startDate, endDate, eventVehicleFilter]);
  useEffect(() => () => eventsRequest.current?.abort(), []);

  // Available sites list from data
  const availableSites = useMemo(() => {
    const sites = new Set(latestData.map((d) => d.site).filter(Boolean));
    return Array.from(sites).sort();
  }, [latestData]);

  const isLoadingTelemetry = loading || refreshing || !!fetchProgress;

  const handleAlertFilterCard = (filter) => {
    if (isLoadingTelemetry) return;
    setAlertFilter((current) => (current === filter ? "ALL" : filter));
  };

  const normalizeAlertText = useCallback((value) => {
    if (Array.isArray(value)) return value.join(" ");
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value).trim();
  }, []);

  const parseNumericValue = useCallback((value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "number" && Number.isFinite(value)) return value;

    const match = String(value).match(/-?\d*\.?\d+(?:e[-+]?\d+)?/i);
    if (!match) return null;

    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const getAlertBucket = useCallback((row) => {
    const alertText = normalizeAlertText(row?.alert).toUpperCase();
    const psi = parseNumericValue(row?.pressure_psi);
    const kpa = parseNumericValue(row?.pressure_kpa);
    const temp = parseNumericValue(row?.temperature_c);

    const isLeakage = alertText.includes("LEAKAGE") || alertText.includes("CRITICAL") || (Number.isFinite(temp) && temp >= 60);
    const isHigh = alertText.includes("HIGH") || (Number.isFinite(psi) && psi > 144) || (Number.isFinite(kpa) && kpa > 993);
    const isLow = alertText.includes("LOW") || (Number.isFinite(psi) && psi < 109) || (Number.isFinite(kpa) && kpa < 750);

    if (isLeakage) return "LEAKAGE";
    if (isHigh) return "HIGH";
    if (isLow) return "LOW";
    return "NORMAL";
  }, [normalizeAlertText, parseNumericValue]);

  const derivedSummary = useMemo(() => {
    const counts = {
      total: latestData.length,
      normal: 0,
      lowPressure: 0,
      highPressure: 0,
      leakage: 0,
      otherAlerts: 0,
      totalAlerts: 0,
    };

    latestData.forEach((row) => {
      const bucket = getAlertBucket(row);
      if (bucket === "LOW") counts.lowPressure += 1;
      else if (bucket === "HIGH") counts.highPressure += 1;
      else if (bucket === "LEAKAGE") counts.leakage += 1;
      else counts.normal += 1;
    });

    counts.totalAlerts = counts.lowPressure + counts.highPressure + counts.leakage;
    return counts;
  }, [latestData]);

  const effectiveSummary = useMemo(() => {
    if (!latestData.length) return summary;
    return derivedSummary;
  }, [latestData.length, derivedSummary, summary]);

  const matchesAlertFilter = useCallback((row, selectedFilter) => {
    const bucket = getAlertBucket(row);

    if (selectedFilter === "ALL") return true;
    if (selectedFilter === "ALERTS_ONLY") return bucket !== "NORMAL";
    if (selectedFilter === "NORMAL") return bucket === "NORMAL";
    if (selectedFilter === "LOW") return bucket === "LOW";
    if (selectedFilter === "HIGH") return bucket === "HIGH";
    if (selectedFilter === "LEAKAGE") return bucket === "LEAKAGE";
    return true;
  }, [getAlertBucket]);

  // Filtered latest rows
  const filteredLatest = useMemo(() => {
    const q = search.trim().toLowerCase();
    return latestData.filter((row) => {
      const matchSearch =
        !q ||
        (row.vehicle_reg && row.vehicle_reg.toLowerCase().includes(q)) ||
        (row.driver && row.driver.toLowerCase().includes(q)) ||
        (row.site && row.site.toLowerCase().includes(q)) ||
        (row.alert && normalizeAlertText(row.alert).toLowerCase().includes(q));

      const matchAlert = matchesAlertFilter(row, alertFilter);
      const matchSite = siteFilter === "ALL" || row.site === siteFilter;

      return matchSearch && matchAlert && matchSite;
    });
  }, [latestData, search, alertFilter, siteFilter, matchesAlertFilter, normalizeAlertText]);

  const downloadTpmsWorkbook = (rows, type, meta = {}) => {
    const sheetRows = (rows || []).map((row) => ({
      "Vehicle Reg": row.vehicle_reg ?? "",
      "Driver Name": row.driver_name ?? row.driver ?? "",
      Site: row.site ?? "",
      "Event Type": row.event_type ?? row.alert ?? "",
      "Pressure (kPa)": row.pressure_kpa ?? "",
      "Pressure (PSI)": row.pressure_psi ?? "",
      "Temperature (°C)": row.temperature_c ?? "",
      "Alert Status": row.alert || "Normal",
      "Date of Event": row.date_of_event ?? row.last_updated ?? "",
      Location: row.location ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows.length ? sheetRows : [{ "Vehicle Reg": "No records" }]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, type === "events" ? "Events" : "Latest");

    const stamp = meta.start || meta.end ? `${meta.start || ""}_${meta.end || ""}` : new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `TPMS_${type}_${stamp.replace(/[^0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}.xlsx`);
  };

  const exportLatestToExcel = () => {
    try {
      downloadTpmsWorkbook(filteredLatest, "latest", { ...appliedReading, search, site: siteFilter, alert: alertFilter });
    } catch { setErrorMsg("Excel export failed. Please try again."); }
  };
  const exportHistoryToExcel = async () => {
    if (!appliedHistory || exporting) return;
    setExporting(true);
    setEventsError("");
    try {
      const allRows = [];
      let exportPage = 1;
      let total = eventsTotal;
      let snapshot = null;
      do {
        const params = new URLSearchParams({ start_date: appliedHistory.start, end_date: appliedHistory.end,
          vehicle_reg: appliedHistory.vehicle || "", page: String(exportPage), page_size: "250" });
        const { data } = await axios.get(`${baseUrl}/api/tpms/events?${params}`, { headers: getHeaders(), timeout: 15000 });
        if (data.loading) throw new Error("The report cache expired. Query events again before exporting.");
        if (!data.success) throw new Error(data.error || "Export failed.");
        if (Number(data.page) !== exportPage) throw new Error("The report changed. Query events again before exporting.");
        if (snapshot && snapshot !== data.lastUpdated) throw new Error("The report refreshed during export. Please try exporting again.");
        snapshot = data.lastUpdated;
        if (!data.rows?.length && allRows.length < data.total) throw new Error("An export page was empty. Please query the report again.");
        allRows.push(...data.rows);
        total = data.total;
        exportPage++;
        await new Promise(resolve => setTimeout(resolve, 0));
      } while (allRows.length < total);
      downloadTpmsWorkbook(allRows, "events", appliedHistory);
    } catch (error) { setEventsError(error.message || "Excel export failed."); }
    finally { setExporting(false); }
  };

  const getPressureClass = (psi) => {
    if (psi === null || psi === undefined || psi === "") return "";
    const p = parseFloat(psi);
    if (p < 109) return "val-danger"; // Under-inflated
    if (p > 144) return "val-warning"; // Over-inflated
    return "val-normal";
  };

  const getTemperatureClass = (temp) => {
    if (temp === null || temp === undefined || temp === "") return "";
    const t = parseFloat(temp);
    if (t >= 60) return "val-danger";
    if (t >= 50) return "val-warning";
    return "val-normal";
  };

  const formatDateTime = (value) => {
    if (!value) return { date: "—", time: "" };

    const raw = String(value).trim();
    if (!raw) return { date: "—", time: "" };

    const [datePart, timePart] = raw.split(" ");
    if (!datePart) return { date: "—", time: "" };

    const d = new Date(raw.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) {
      const fallback = datePart.includes("-") ? datePart.split("-") : datePart.split("/");
      if (fallback.length === 3) {
        const [y, m, day] = fallback[0].length === 4 ? [fallback[0], fallback[1], fallback[2]] : [fallback[2], fallback[1], fallback[0]];
        return {
          date: `${day}/${m}/${y}`,
          time: timePart || "",
        };
      }
      return { date: datePart, time: timePart || "" };
    }

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");

    return {
      date: `${day}/${month}/${year}`,
      time: `${hh}:${mm}:${ss}`,
    };
  };

  return (
    <div className="tpms-page bt-container">
      {/* Modern Standard Header */}
      <div className="bt-header">
        <div className="bt-header-main">
          <div className="bt-header-left">
            <button
              className="bt-back-btn"
              onClick={() => navigate("/main")}
              title="Back"
            >
              <FaArrowLeft />
            </button>
            <div className="bt-header-title">
              <h1>
                <FaTachometerAlt style={{ color: "#2563eb", marginRight: 8, fontSize: "1.25rem" }} />
                Tire Pressure Monitoring System
              </h1>
            </div>
          </div>

          <div className="bt-header-actions">
            <button
              className="tpms-refresh-btn"
              onClick={() => fetchLatest(true)}
              disabled={refreshing || loading}
              title={refreshing ? "Updating telemetry…" : "Refresh telemetry"}
              aria-label="Refresh telemetry"
            >
              <FaSyncAlt className={refreshing ? "spinning" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bt-main-content">
        {fetchProgress && (
          <div className="tpms-notice-banner" role="status">
            {fetchProgress.total
              ? `Checking TPMS: ${fetchProgress.checked} of ${fetchProgress.total} vehicles. This may take a few minutes.`
              : "Connecting to TPMS and loading the vehicle list..."}
          </div>
        )}
        {errorMsg && (
          <div className="tpms-error-banner">
            <FaExclamationTriangle /> {errorMsg}
          </div>
        )}

        {activeTab === "latest" && <>
        {/* Top Filter & Search Toolbar - Above KPI Cards */}
        <div className="tpms-toolbar tpms-top-toolbar">
          <div className="search-wrap">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search vehicle, driver, site, alert…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>
              <FaFilter /> Alert Status:
            </label>
            <select aria-label="Alert status" value={alertFilter} onChange={(e) => setAlertFilter(e.target.value)}>
              <option value="ALL">All Statuses ({latestData.length})</option>
              <option value="ALERTS_ONLY">Alerts Only ({effectiveSummary.totalAlerts})</option>
              <option value="NORMAL">Normal ({effectiveSummary.normal})</option>
              <option value="LOW">Low Pressure ({effectiveSummary.lowPressure})</option>
              <option value="HIGH">High Pressure ({effectiveSummary.highPressure})</option>
              <option value="LEAKAGE">Leakage ({effectiveSummary.leakage})</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Site:</label>
            <select aria-label="Site" value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
              <option value="ALL">All Sites</option>
              {availableSites.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="export-actions">
            <button className="export-btn btn-excel" onClick={exportLatestToExcel} disabled={loading || refreshing || !filteredLatest.length} title="Export filtered readings to Excel">
              <FaFileExcel /> Excel
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <section className="tpms-kpis">
          <div className="kpi-card card-total" aria-label="Monitored vehicles summary">
            <div className="kpi-icon-wrap">
              <FaTruck />
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Monitored Vehicles</span>
              <strong className="kpi-value">{isLoadingTelemetry ? "—" : effectiveSummary.total}</strong>
            </div>
          </div>

          <div
            className={`kpi-card card-normal ${alertFilter === "NORMAL" ? "kpi-card-active" : ""}`}
            onClick={() => handleAlertFilterCard("NORMAL")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleAlertFilterCard("NORMAL");
              }
            }}
            aria-label="Filter normal pressure vehicles"
          >
            <div className="kpi-icon-wrap">
              <FaCheckCircle />
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Normal Pressure</span>
              <strong className="kpi-value">{isLoadingTelemetry ? "—" : effectiveSummary.normal}</strong>
            </div>
          </div>

          <div
            className={`kpi-card card-low ${alertFilter === "LOW" ? "kpi-card-active" : ""}`}
            onClick={() => handleAlertFilterCard("LOW")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleAlertFilterCard("LOW");
              }
            }}
            aria-label="Filter low pressure vehicles"
          >
            <div className="kpi-icon-wrap">
              <FaExclamationTriangle />
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Low Pressure (&lt;109 PSI)</span>
              <strong className="kpi-value">{isLoadingTelemetry ? "—" : effectiveSummary.lowPressure}</strong>
            </div>
          </div>

          <div
            className={`kpi-card card-high ${alertFilter === "HIGH" ? "kpi-card-active" : ""}`}
            onClick={() => handleAlertFilterCard("HIGH")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleAlertFilterCard("HIGH");
              }
            }}
            aria-label="Filter high pressure vehicles"
          >
            <div className="kpi-icon-wrap">
              <FaExclamationTriangle />
            </div>
            <div className="kpi-info">
              <span className="kpi-label">High Pressure (&gt;144 PSI)</span>
              <strong className="kpi-value">{isLoadingTelemetry ? "—" : effectiveSummary.highPressure}</strong>
            </div>
          </div>

          <div
            className={`kpi-card card-leakage ${alertFilter === "LEAKAGE" ? "kpi-card-active" : ""}`}
            onClick={() => handleAlertFilterCard("LEAKAGE")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleAlertFilterCard("LEAKAGE");
              }
            }}
            aria-label="Filter leakage and critical vehicles"
          >
            <div className="kpi-icon-wrap">
              <FaThermometerHalf />
            </div>
            <div className="kpi-info">
              <span className="kpi-label">Leakage / Critical</span>
              <strong className="kpi-value">{isLoadingTelemetry ? "—" : effectiveSummary.leakage}</strong>
            </div>
          </div>
        </section>

        </>}
        {/* Latest readings view only */}

        {/* TAB 1: LATEST READINGS */}
        {activeTab === "latest" && (
          <section className="tpms-main-view">
            {/* Table */}
            <div className="tpms-table-wrap">
            {loading ? (
              <div className="tpms-loading">
                <FaSyncAlt className="spinning" /> Loading TPMS data…
              </div>
            ) : filteredLatest.length === 0 ? (
              <div className="tpms-empty">
                {errorMsg ? (
                  <div>
                    <FaExclamationTriangle style={{ fontSize: "28px", color: "#dc2626", marginBottom: "10px" }} />
                    <p style={{ fontWeight: 600, color: "#1e293b", fontSize: "15px" }}>Live TPMS Telemetry Unavailable</p>
                    <p style={{ color: "#64748b", maxWidth: "520px", margin: "0 auto 16px" }}>{errorMsg}</p>
                    <button className="tpms-retry-btn" onClick={() => fetchLatest(true)}>
                      <FaSyncAlt /> Retry Connection
                    </button>
                  </div>
                ) : (
                  "No TPMS readings match your search or filter criteria."
                )}
              </div>
            ) : (
              <table className="tpms-table tpms-readings-table">
                <colgroup>{[9,16,18,8,8,9,13,12,7].map((width, i) => <col key={i} style={{width:`${width}%`}} />)}</colgroup>
                <thead>
                  <tr>
                    <th scope="col">Vehicle Reg</th>
                    <th scope="col">Site</th>
                    <th scope="col">Driver</th>
                    <th scope="col">Pressure (kPa)</th>
                    <th scope="col">Pressure (PSI)</th>
                    <th scope="col">Temperature</th>
                    <th scope="col">Alert Status</th>
                    <th scope="col">Last Updated</th>
                    <th scope="col">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLatest.map((row, idx) => {
                    const hasAlert = Boolean(row.alert);
                    const psiClass = getPressureClass(row.pressure_psi);
                    const tempClass = getTemperatureClass(row.temperature_c);

                    return (
                      <tr key={idx} className={hasAlert ? "row-has-alert" : ""}>
                        <td className="cell-vehicle">
                          <strong>{row.vehicle_reg}</strong>
                        </td>
                        <td>{row.site || "—"}</td>
                        <td>{row.driver || "—"}</td>
                        <td className="cell-num">{row.pressure_kpa !== null ? `${row.pressure_kpa} kPa` : "—"}</td>
                        <td className="cell-num">
                          {row.pressure_psi !== null ? (
                            <span className={`badge-pill ${psiClass}`}>{row.pressure_psi} PSI</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="cell-num">
                          {row.temperature_c !== null ? (
                            <span className={`badge-pill ${tempClass}`}>{row.temperature_c} °C</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {row.alert ? (
                            <span className="badge-alert">
                              <FaExclamationTriangle /> {row.alert}
                            </span>
                          ) : (
                            <span className="badge-normal">
                              <FaCheckCircle /> Normal
                            </span>
                          )}
                        </td>
                        <td className="cell-date">
                          {(() => {
                            const { date, time } = formatDateTime(row.last_updated);
                            return (
                              <div className="tpms-date-stack">
                                <span>{date}</span>
                                {time ? <small>{time}</small> : null}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="cell-location">
                          {row.location ? (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(row.location)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="location-link"
                              title="View on Google Maps"
                            >
                              <FaMapMarkerAlt /> View map
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="table-footer">
            <span>Showing {filteredLatest.length} of {latestData.length} vehicles</span>
            {lastUpdated && <span>Last synced: {lastUpdated.slice(0, 19).replace("T", " ")}</span>}
          </div>
        </section>
      )}

      {/* TAB 2: EVENTS & HISTORY LOG */}
      {activeTab === "events" && (
        <section className="tpms-main-view">
          <div className="tpms-toolbar">
            <div className="filter-group">
              <label>Start Date:</label>
              <input
                aria-label="History start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>End Date:</label>
              <input
                aria-label="History end date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Vehicle Reg:</label>
              <input
                type="text"
                placeholder="Filter by Reg (optional)"
                value={eventVehicleFilter}
                onChange={(e) => setEventVehicleFilter(e.target.value)}
              />
            </div>

            <button className="tpms-filter-btn" onClick={() => fetchEvents(1, { start: startDate, end: endDate, vehicle: eventVehicleFilter.trim() })} disabled={exporting}>
              {eventsLoading ? "Fetching…" : "Query Events"}
            </button>
          </div>

          <div className="tpms-report-context">
            <span>{appliedHistory ? `Applied dates: ${appliedHistory.start} to ${appliedHistory.end}` : "Choose dates and query events."} | Maximum 31 days per report.</span>
            <button className="export-btn btn-excel" disabled={!eventsTotal || eventsLoading || exporting} onClick={exportHistoryToExcel}><FaFileExcel /> {exporting ? "Preparing Excel..." : "Download Excel"}</button>
          </div>
          {appliedHistory && (startDate !== appliedHistory.start || endDate !== appliedHistory.end || eventVehicleFilter.trim() !== appliedHistory.vehicle) && <p className="tpms-filter-note">Filters changed. Click Query Events to apply them.</p>}
          {historyProgress && <div className="tpms-notice-banner" role="status">{historyProgress.total ? `Preparing report: ${historyProgress.checked} of ${historyProgress.total} batches complete.` : "Preparing event report..."}</div>}
          <div className="tpms-table-wrap" tabIndex={0} aria-label="Event history table">
            {eventsLoading ? (
              <div className="tpms-loading">
                <FaSyncAlt className="spinning" /> Fetching event telemetry…
              </div>
            ) : eventsError ? (
              <div className="tpms-error-banner">{eventsError}</div>
            ) : events.length === 0 ? (
              <div className="tpms-empty">No events found in selected date range.</div>
            ) : (
              <table className="tpms-table">
                <thead>
                  <tr>
                    <th scope="col">Vehicle Reg</th>
                    <th scope="col">Driver Name</th>
                    <th scope="col">Site</th>
                    <th scope="col">Event Type</th>
                    <th scope="col">Odometer</th>
                    <th scope="col">Date of Event</th>
                    <th scope="col">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedEvents.map((evt, idx) => (
                    <tr key={`${evt.vehicle_reg}-${evt.date_of_event}-${idx}`}>
                      <td className="cell-vehicle">
                        <strong>{evt.vehicle_reg}</strong>
                      </td>
                      <td>{evt.driver_name || "—"}</td>
                      <td>{evt.site || "—"}</td>
                      <td>
                        <span className="badge-event-type">{evt.event_type}</span>
                      </td>
                      <td className="cell-num">{evt.odometer || "—"}</td>
                      <td className="cell-date">
                        {(() => {
                          const { date, time } = formatDateTime(evt.date_of_event);
                          return (
                            <div className="tpms-date-stack">
                              <span>{date}</span>
                              {time ? <small>{time}</small> : null}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="cell-location">
                        {evt.location ? (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(evt.location)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="location-link"
                          >
                            <FaMapMarkerAlt /> View map
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="table-footer">
            <span>Showing {eventsTotal ? (safePageNumber - 1) * pageSize + 1 : 0}-{Math.min(safePageNumber * pageSize, eventsTotal)} of {eventsTotal.toLocaleString()} events</span>
            {eventsTotal > pageSize && (
              <div className="tpms-pagination">
                <button disabled={eventsLoading || exporting || safePageNumber <= 1} onClick={() => {
                  const next = sanitizeHistoryPage(safePageNumber - 1);
                  fetchEvents(next);
                }}>Previous</button>
                <span>Page {safePageNumber} of {historyPageCount}</span>
                <button disabled={eventsLoading || exporting || safePageNumber >= historyPageCount} onClick={() => {
                  const next = sanitizeHistoryPage(safePageNumber + 1);
                  fetchEvents(next);
                }}>Next</button>
              </div>
            )}
          </div>
        </section>
      )}

      </div>
    </div>
  );
};

export default TpmsMonitoring;

